import { useEffect, useState } from "react";

type SingleTabMessage = { type: "HELLO" | "ACTIVE" };

export function useSingleTabGuard(): boolean {
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let isLeader = false;
    const channel = new BroadcastChannel("single_tab_guard");

    const blockSelf = () => {
      if (blocked) return;
      // Try to close if opened programmatically; fallback to blocking overlay
      try {
        window.close();
      } catch (_) {
        // ignore
      }
      // alert("This app is already open in another tab.");
      setBlocked(true);
    };

    let helloTimer: number | undefined;

    channel.onmessage = (event: MessageEvent<SingleTabMessage>) => {
      const data = event.data;
      if (!data) return;
      if (data.type === "HELLO") {
        if (isLeader) {
          channel.postMessage({ type: "ACTIVE" });
        }
      } else if (data.type === "ACTIVE") {
        if (helloTimer) {
          clearTimeout(helloTimer);
          helloTimer = undefined;
        }
        blockSelf();
      }
    };

    // Announce presence and wait briefly for an ACTIVE response.
    channel.postMessage({ type: "HELLO" });
    helloTimer = window.setTimeout(() => {
      // If no ACTIVE response is received within the timeout,
      // this tab assumes leadership and will respond to future HELLOs.
      isLeader = true;
    }, 600);

    return () => {
      if (helloTimer) clearTimeout(helloTimer);
      channel.close();
    };
  }, [blocked]);

  return blocked;
}

export function useSingleTabGuardStatus(channelName: string = "single_tab_guard"): { blocked: boolean; ready: boolean } {
  const [blocked, setBlocked] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let isLeader = false;
    const channel = new BroadcastChannel(channelName);

    const blockSelf = () => {
      if (blocked) return;
      try {
        window.close();
      } catch (_) {
      setBlocked(true);
      setReady(true);
      }
    };

    let helloTimer: number | undefined;

    channel.onmessage = (event: MessageEvent<SingleTabMessage>) => {
      const data = event.data;
      if (!data) return;
      if (data.type === "HELLO") {
        if (isLeader) {
          channel.postMessage({ type: "ACTIVE" });
        }
      } else if (data.type === "ACTIVE") {
        if (helloTimer) {
          clearTimeout(helloTimer);
          helloTimer = undefined;
        }
        blockSelf();
      }
    };

    channel.postMessage({ type: "HELLO" });
    helloTimer = window.setTimeout(() => {
      isLeader = true;
      setReady(true);
    }, 600);

    return () => {
      if (helloTimer) clearTimeout(helloTimer);
      channel.close();
    };
  }, [blocked, channelName]);

  return { blocked, ready };
}